import React, { useState, useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * MockModeBanner — Shows a persistent warning banner when portal mocking is active.
 * Checks GET /api/njmmis/live/status for integration_mode === "mock".
 */
const MockModeBanner = ({ token, portalName = "Portal" }) => {
  const [isMock, setIsMock] = useState(false);

  useEffect(() => {
    if (!token) return;
    let auth401Streak = 0;
    let cancelled = false;
    const check = async () => {
      try {
        // Read fresh token — the global axios interceptor silently rotates
        // ``sessionStorage.token`` when the access JWT expires, but our
        // raw ``fetch`` call doesn't go through that interceptor.  Pull
        // the latest token every poll so we don't keep using a stale one.
        const currentToken = sessionStorage.getItem('token') || token;
        const res = await fetch(`${API}/api/njmmis/live/status`, {
          headers: { Authorization: `Bearer ${currentToken}` },
        });
        if (res.status === 401) {
          auth401Streak += 1;
          return;
        }
        auth401Streak = 0;
        if (res.ok) {
          const data = await res.json();
          if (data?.data?.integration_mode === 'mock') {
            setIsMock(true);
          } else {
            setIsMock(false);
          }
        }
      } catch (e) {
        // Silently ignore network errors
      }
    };
    check();
    // Re-check every 30s in case toggle was flipped.  Quit after 3
    // consecutive 401s so a dead session doesn't flood the console.
    const interval = setInterval(() => {
      if (cancelled || auth401Streak >= 3) {
        clearInterval(interval);
        return;
      }
      check();
    }, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [token]);

  if (!isMock) return null;

  return (
    <div
      data-testid="mock-mode-banner"
      className="flex items-center gap-2 px-4 py-2 mb-3 bg-amber-50 border border-amber-300 rounded-lg text-amber-800 text-sm font-medium"
    >
      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
      <span>Mock Mode Active — {portalName} responses are simulated. No real portal connections.</span>
    </div>
  );
};

export default MockModeBanner;
